import type { NumericComparisonOperator } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../../../../components-v2/Combobox';
import { Input } from '../../../../../../../components-v2/Input';
import { bs } from '../../../../../../../styles/styles';
import { styleSheet } from './ComparisonRow.styles';
import { COMPARISON_OPERATOR_OPTIONS, clampThreshold } from './conditionalVisibilitySources';

type OperatorOption = { value: NumericComparisonOperator; label: string };

export interface ComparisonRowProps {
	operator: NumericComparisonOperator;
	threshold: string;
	bounds?: { min: number; max: number };
	disabled: boolean;
	onOperatorChange: (operator: NumericComparisonOperator) => void;
	onThresholdChange: (threshold: string) => void;
}

export const ComparisonRow: React.FC<ComparisonRowProps> = React.memo(
	({ operator, threshold, bounds, disabled, onOperatorChange, onThresholdChange }) => {
		const selectedOperator = React.useMemo(
			() => COMPARISON_OPERATOR_OPTIONS.find(o => o.value === operator) ?? null,
			[operator]
		);

		return (
			<div className={css(styleSheet.row)}>
				<div className={css(styleSheet.operator)}>
					<Combobox
						items={COMPARISON_OPERATOR_OPTIONS as OperatorOption[]}
						value={selectedOperator}
						onValueChange={value => {
							const option = value as OperatorOption | null;
							if (option) onOperatorChange(option.value);
						}}
						itemToStringLabel={value => (value as OperatorOption).label}
						isItemEqualToValue={(item, value) =>
							(item as OperatorOption)?.value === (value as OperatorOption | null)?.value
						}
						disabled={disabled}
					>
						<ComboboxInput
							placeholder='Select comparison'
							aria-label='Select comparison operator'
							disabled={disabled}
							styles={[styleSheet.operatorInput]}
						/>
						<ComboboxContent>
							<ComboboxEmpty>No comparisons found</ComboboxEmpty>
							<ComboboxList>
								{(option: OperatorOption) => (
									<ComboboxItem key={option.value} value={option}>
										<ComboboxItemRadioIndicator />
										<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>{option.label}</span>
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				{/* step='any': the default steps by 1, so a decimal sits in stepMismatch and aborts the save. */}
				<Input
					type='number'
					step='any'
					inputMode='numeric'
					value={threshold}
					min={bounds?.min}
					max={bounds?.max}
					disabled={disabled}
					aria-label='Comparison value'
					onChange={event => onThresholdChange(clampThreshold(event.target.value, bounds))}
					styles={[styleSheet.threshold]}
				/>
			</div>
		);
	}
);

ComparisonRow.displayName = 'ComparisonRow';
